import { CHECKOUT_SUMMARY_QUERY_KEY } from "@/components/Checkout/hooks";
import { USER_PLAN_QUERY_KEY } from "@/components/Root/hooks/useUserPlanData";
import { useNavigate } from "@/lib/router";
import { captureSentryException } from "@/services/sentry";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import toast from "react-hot-toast";
import { useAuth } from "reactfire";

const LOGOUT_CACHE_CLEAR_DELAY_MS = 600;

/** Queries keyed without a uid, so they must be dropped when the user changes */
export const USER_SCOPED_QUERY_KEYS = [
  ["user"],
  [USER_PLAN_QUERY_KEY],
  ["pricing-data"],
  [CHECKOUT_SUMMARY_QUERY_KEY],
] as const;

type TLogoutOptions = {
  redirectTo?: string | false;
};

const useLogoutHandler = ({ redirectTo = "/auth" }: TLogoutOptions = {}) => {
  const client = useQueryClient();
  const auth = useAuth();
  const navigate = useNavigate();

  const clearCacheOnLogout = useCallback(() => {
    USER_SCOPED_QUERY_KEYS.forEach((queryKey) =>
      client.removeQueries({ queryKey }),
    );
  }, [client]);

  const handleLogout = useCallback(() => {
    const signOutAndRedirect = async () => {
      await auth.signOut();
      toast.success("Logged out successfully");
      setTimeout(clearCacheOnLogout, LOGOUT_CACHE_CLEAR_DELAY_MS);
      if (redirectTo) {
        await navigate(redirectTo);
      }
    };

    signOutAndRedirect().catch((error: unknown) => {
      captureSentryException(error);
      toast.error("Couldn't log out. Please try again.");
    });
  }, [auth, clearCacheOnLogout, navigate, redirectTo]);

  return { clearCacheOnLogout, handleLogout };
};

export default useLogoutHandler;
